import React, { FC, useCallback, useState } from 'react';

import { Button, LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { AccountDetailsView } from '../../components/AccountDetailsView';
import AccountAuthorizedCustomers from '../../components/AccountDetailsView/AccountAuthorizedCustomers';
import { AccountInterestDetails } from '../../components/AccountDetailsView/AccountInterestDetails';
import { AccountNameBox, AccountNameBoxSkeleton } from '../../components/AccountNameBox';
import AddAuthorizedCustomerModal from '../../components/AddAuthorizedCustomer/AddAuthorizedCustomerModal';
import CloseAccountModal from '../../components/CloseAccount/CloseAccountModal';
import AccountNotFound from '../../components/shared/AccountNotFound/AccountNotFound';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { useAccount, useAccountFreeWithdrawals } from '../../fetchHooks/useAccount';
import { useAuthorizedCustomers } from '../../fetchHooks/useAuthorizedCustomers';
import { useDebitcardLink } from '../../fetchHooks/useCards';
import useInterests from '../../fetchHooks/useInterests';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useTranslation from '../../hooks/useTranslation';
import AccountCardsApp from '../AccountCards/AccountCards';
import { useAccountSupportsCards } from './useAccountSupportsCards';

import './AccountDetails.scss';

const AccountDetailsApp: FC = () => {
    const { featureToggles } = useConfig();
    const accountId = useAccountId();
    const translated = useTranslation();
    const interestQuery = useInterests(accountId);
    const { data: account, ...accountState } = useAccount(accountId);

    const authorizedCustomersQuery = useAuthorizedCustomers(accountId, {
        enabled: Boolean(account?.additionalInfo.authorizedcustomers),
    });
    const canHaveCards = useAccountSupportsCards(account);
    const { data: cards } = useDebitcardLink(account?.accountNumber, { enabled: canHaveCards });
    const [showCloseAccountModal, setShowCloseAccountModal] = useState(false);
    const [showAddAuthorizedCustomerModal, setShowAddAuthorizedCustomerModal] = useState(false);

    const { data: freeWithdrawals, ...freeWithdrawalstate } = useAccountFreeWithdrawals(
        account,
        featureToggles.enableGettingAccountWithdrawalsInAccountDetails,
    );

    const refresh = useCallback(() => {
        if (accountState.isError) accountState.refetch();
        if (interestQuery.isError) interestQuery.refetch();
        if (authorizedCustomersQuery.isError) authorizedCustomersQuery.refetch();
    }, [accountState, authorizedCustomersQuery, interestQuery]);

    const accountInfoIsLoading = accountState.isLoading || freeWithdrawalstate.isLoading;

    if (accountState.error?.errorName === 'ACCOUNT_NOT_FOUND') {
        return <AccountNotFound />;
    }

    if (accountState.error || interestQuery.error) {
        const errorMessage = accountState.error || interestQuery.error;

        return (
            <Content>
                <ErrorCardWrapper
                    exception={errorMessage}
                    defaultError={translated.accountDetails.accountDetailsErrorMessage}
                >
                    <Button onClick={refresh}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            </Content>
        );
    }

    return (
        <Content>
            {!accountState.isLoading && account !== undefined ? (
                <AccountNameBox account={account} />
            ) : (
                <AccountNameBoxSkeleton />
            )}
            <>
                <AccountAuthorizedCustomers
                    authorizedCustomers={authorizedCustomersQuery.data}
                    account={account}
                    isLoading={accountState.isLoading || authorizedCustomersQuery.isLoading}
                />
                {authorizedCustomersQuery.isFetched &&
                    featureToggles.enableAddAuthorizedCustomer &&
                    account &&
                    account.rights.addAuthorizeCustomers && (
                        <>
                            <div className="account-details__link-container">
                                <LinkButton
                                    icon={IconTypes.PERSONER_24}
                                    onClick={() => setShowAddAuthorizedCustomerModal(true)}
                                >
                                    {translated.accountDetails.addAuthorizedCustomer.shareAccount}
                                </LinkButton>
                            </div>
                            <AddAuthorizedCustomerModal
                                show={showAddAuthorizedCustomerModal}
                                accountId={account.accountId}
                                setShow={setShowAddAuthorizedCustomerModal}
                            />
                        </>
                    )}
            </>
            {cards && cards?.length > 0 && <AccountCardsApp cards={cards} account={account} />}
            <AccountDetailsView account={account} freeWithdrawals={freeWithdrawals} isLoading={accountInfoIsLoading} />
            <AccountInterestDetails
                interests={interestQuery.data}
                currency={account?.currency ?? 'NOK'}
                isLoading={!(interestQuery.isSuccess && interestQuery.data)}
            />
            {featureToggles.enableCloseAccount && accountId && (
                <>
                    <div className="account-details__link-container">
                        <LinkButton onClick={() => setShowCloseAccountModal(true)}>
                            {translated.closeAccount.title}
                        </LinkButton>
                    </div>
                    <CloseAccountModal
                        accountId={accountId}
                        show={showCloseAccountModal}
                        cards={cards}
                        setShow={setShowCloseAccountModal}
                    />
                </>
            )}
        </Content>
    );
};

export default AccountDetailsApp;
